/**
 * Configuration des donnes d'entraînement libre (amont + in-game).
 * Preneur, force Chavey, qualités par siège — source de vérité unique.
 */

import { useCallback, useMemo, type CSSProperties } from 'react';
import { StrengthBoostIcon } from './Icons';
import { Switch, SwitchRow } from './Switch';
import type { HandQuality, TrainingAIModelSelection, TrainingAIModelSlot } from '../types/app';
import type { GameSpeed } from '../pages/TrainingPage';
import {
  CHAVEY_CRANK_SCORES,
  CHAVEY_CRANK_SHORT,
  CHAVEY_CUSTOM_SLIDER_MAX,
  chaveyLabelForScore,
  chaveyScoreToSeuil,
  chaveySeuilToScore,
  clampChaveyCustomScore,
  isClassicChaveyCrank,
  isTakerConstraintActive,
  validateHandQualities,
  type ChaveySeuil,
  type TrainingDealSetupValue,
} from '../utils/trainingDealSetup';

const HAND_QUALITY_OPTIONS: { value: HandQuality; label: string }[] = [
  { value: 'random', label: 'Aléatoire' },
  { value: 'passe', label: 'Passe (jeu faible)' },
  { value: 'petite', label: 'Petite' },
  { value: 'garde', label: 'Garde' },
  { value: 'garde_sans', label: 'Garde sans' },
  { value: 'garde_contre', label: 'Garde contre' },
];

const POSITION_NAMES: Record<number, string> = {
  0: 'Sud (vous)',
  1: 'Est',
  2: 'Nord',
  3: 'Ouest',
};

const SPEED_OPTIONS: { value: GameSpeed; label: string }[] = [
  { value: 'fast', label: 'Rapide' },
  { value: 'normal', label: 'Normal' },
  { value: 'slow', label: 'Lent' },
  { value: 'click', label: 'Clic' },
];

const AI_MODEL_OPTIONS: { value: TrainingAIModelSlot; label: string }[] = [
  { value: 'production', label: 'ML Production' },
  { value: 'beta', label: 'ML Beta' },
  { value: 'legacy-rv', label: 'Legacy RV' },
];

const CONTRACT_OPTIONS = [
  { value: 1, label: 'Petite' },
  { value: 2, label: 'Garde' },
  { value: 3, label: 'Garde sans' },
  { value: 4, label: 'Garde contre' },
];

export interface TrainingDealSetupProps {
  value: TrainingDealSetupValue;
  onChange: (next: TrainingDealSetupValue) => void;
  /** Affichage densifié (sidebar) ou plus aéré (accueil). */
  variant?: 'home' | 'sidebar';
  /** Sections session (vitesse / bots / IA) — optionnelles, pilotées par le parent. */
  gameSpeed?: GameSpeed;
  onSpeedChange?: (speed: GameSpeed) => void;
  disabledBots?: Set<number>;
  onToggleBot?: (seat: number) => void;
  humanAutomated?: boolean;
  aiModelSelection?: TrainingAIModelSelection;
  onAIModelSelectionChange?: (selection: TrainingAIModelSelection) => void;
  isAdmin?: boolean;
  /** Forcer un contrat pendant les enchères (in-game uniquement). */
  showForceBid?: boolean;
  forceSeat?: number;
  forceContract?: number;
  onForceSeatChange?: (seat: number) => void;
  onForceContractChange?: (contract: number) => void;
  onForceBid?: () => void;
  players?: Array<{ pseudo: string; position: number }>;
  /** Masquer la section qualités par siège (mode compact). */
  showHandQualities?: boolean;
}

function sliderTickStyleFraction(fraction: number): CSSProperties {
  return {
    left: `calc(var(--nb-donnes-thumb-half) + (100% - 2 * var(--nb-donnes-thumb-half)) * ${fraction})`,
  };
}

function ChaveyStrengthSlider({
  custom,
  seuil,
  minScore,
  onCustomChange,
  onSeuilChange,
  onScoreChange,
}: {
  custom: boolean;
  seuil: ChaveySeuil;
  minScore: number;
  onCustomChange: (custom: boolean) => void;
  onSeuilChange: (seuil: ChaveySeuil) => void;
  onScoreChange: (score: number) => void;
}) {
  const displayScore = custom ? minScore : chaveySeuilToScore(seuil);
  const boosted = custom && displayScore > 0 && !isClassicChaveyCrank(displayScore);
  const displayLabel = chaveyLabelForScore(displayScore, custom);
  const fillFraction = custom
    ? displayScore / CHAVEY_CUSTOM_SLIDER_MAX
    : seuil / (CHAVEY_CRANK_SCORES.length - 1);

  const handleSliderChange = (raw: number) => {
    if (custom) {
      onScoreChange(clampChaveyCustomScore(raw));
    } else {
      onSeuilChange(raw as ChaveySeuil);
    }
  };

  const handleTickClick = (score: number) => {
    if (custom) {
      onScoreChange(score);
    } else {
      onSeuilChange(chaveyScoreToSeuil(score));
    }
  };

  return (
    <div className="nb-donnes-selector min-strength-selector training-chavey-slider">
      <div className="nb-donnes-header">
        <span className="nb-donnes-value min-strength-value">
          {displayLabel}
          {boosted && (
            <span
              className="min-strength-boost-wrap"
              title={`${displayScore} points Chavey (entre deux crans)`}
            >
              <StrengthBoostIcon size={14} className="min-strength-boost-icon" />
            </span>
          )}
        </span>
        <label className="nb-donnes-custom-toggle">
          Personnaliser
          <Switch compact checked={custom} onChange={onCustomChange} />
        </label>
      </div>
      <div
        className="nb-donnes-slider-wrap"
        style={{ '--fill-fraction': fillFraction } as CSSProperties}
      >
        <div className="nb-donnes-track" />
        <div className="nb-donnes-track-fill" />
        <div className="nb-donnes-thumb" aria-hidden="true" />
        <input
          type="range"
          className="nb-donnes-slider"
          min={0}
          max={custom ? CHAVEY_CUSTOM_SLIDER_MAX : CHAVEY_CRANK_SCORES.length - 1}
          step={1}
          value={custom ? displayScore : seuil}
          onChange={(e) => handleSliderChange(parseInt(e.target.value, 10))}
        />
        <div className="nb-donnes-ticks">
          {CHAVEY_CRANK_SCORES.map((score, i) => {
            const tickActive = custom ? displayScore === score : seuil === i;
            const tickFilled = custom ? displayScore >= score : seuil >= i;
            return (
              <div
                key={score}
                className={`nb-donnes-tick major${tickActive ? ' active' : ''}${tickFilled ? ' filled' : ''}`}
                style={sliderTickStyleFraction(
                  custom ? score / CHAVEY_CUSTOM_SLIDER_MAX : i / (CHAVEY_CRANK_SCORES.length - 1),
                )}
                onClick={() => handleTickClick(score)}
              >
                <div className="nb-donnes-tick-mark" />
                <span className="nb-donnes-tick-label">
                  {custom && i === 0 ? 'Passe' : CHAVEY_CRANK_SHORT[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function TrainingDealSetup({
  value,
  onChange,
  variant = 'home',
  gameSpeed,
  onSpeedChange,
  disabledBots,
  onToggleBot,
  humanAutomated,
  aiModelSelection,
  onAIModelSelectionChange,
  isAdmin,
  showForceBid,
  forceSeat = 0,
  forceContract = 2,
  onForceSeatChange,
  onForceContractChange,
  onForceBid,
  players,
  showHandQualities = true,
}: TrainingDealSetupProps) {
  const constraintValidation = useMemo(
    () => validateHandQualities(value.handQualities),
    [value.handQualities],
  );

  const takerActive = isTakerConstraintActive(value);

  const patch = useCallback(
    (partial: Partial<TrainingDealSetupValue>) => {
      onChange({ ...value, ...partial });
    },
    [onChange, value],
  );

  const handleHandQualityChange = useCallback(
    (seat: number, quality: HandQuality) => {
      patch({
        handQualities: { ...value.handQualities, [seat]: quality },
      });
    },
    [patch, value.handQualities],
  );

  const handleAIModelChange = useCallback(
    (key: keyof TrainingAIModelSelection, slot: TrainingAIModelSlot) => {
      if (!aiModelSelection || !onAIModelSelectionChange) return;
      onAIModelSelectionChange({ ...aiModelSelection, [key]: slot });
    },
    [aiModelSelection, onAIModelSelectionChange],
  );

  const rootClass =
    variant === 'home'
      ? 'training-deal-setup training-deal-setup--home'
      : 'training-deal-setup training-deal-setup--sidebar';

  return (
    <div className={rootClass}>
      <section className="training-deal-setup-section">
        <h4 className="training-deal-setup-title">Preneur et force du jeu</h4>
        <p className="training-deal-setup-hint">
          Barème Chavey : la distribution favorise une main assez forte pour le siège choisi.
          À « Hasard pur », les cartes sont entièrement aléatoires.
        </p>

        <div className="training-hand-config-row">
          <label htmlFor="training-taker-seat">Preneur</label>
          <select
            id="training-taker-seat"
            className="training-select"
            value={value.taker}
            disabled={!takerActive}
            onChange={(e) => patch({ taker: Number(e.target.value) })}
          >
            {[0, 1, 2, 3].map((seat) => (
              <option key={seat} value={seat}>
                {POSITION_NAMES[seat]}
              </option>
            ))}
          </select>
        </div>

        <ChaveyStrengthSlider
          custom={value.chaveyCustom}
          seuil={value.chaveySeuil}
          minScore={value.chaveyMinScore}
          onCustomChange={(custom) => {
            if (custom) {
              const score = value.chaveySeuil > 0
                ? chaveySeuilToScore(value.chaveySeuil)
                : 40;
              patch({ chaveyCustom: true, chaveyMinScore: score });
            } else {
              patch({
                chaveyCustom: false,
                chaveySeuil: value.chaveyMinScore > 0
                  ? chaveyScoreToSeuil(value.chaveyMinScore)
                  : value.chaveySeuil,
              });
            }
          }}
          onSeuilChange={(seuil) => patch({ chaveySeuil: seuil, chaveyCustom: false })}
          onScoreChange={(score) => patch({ chaveyMinScore: score, chaveyCustom: true })}
        />

        <SwitchRow
          className="training-deal-setup-check"
          checked={value.guaranteeTaker}
          disabled={!takerActive}
          onChange={(next) => patch({ guaranteeTaker: next })}
          label="Garantir que ce siège prenne (les autres passent)"
        />
      </section>

      {showHandQualities && (
        <section className="training-deal-setup-section">
          <h4 className="training-deal-setup-title">Force par siège (avancé)</h4>
          <p className="training-deal-setup-hint">
            Contraint chaque main via le barème Chavey. Si un preneur est aussi défini,
            la distribution privilégie d&apos;abord sa force minimale.
          </p>
          {[0, 1, 2, 3].map((seat) => (
            <div key={seat} className="training-hand-config-row">
              <label>{POSITION_NAMES[seat]}</label>
              <select
                className="training-select"
                value={value.handQualities[seat] ?? 'random'}
                onChange={(e) => handleHandQualityChange(seat, e.target.value as HandQuality)}
              >
                {HAND_QUALITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {!constraintValidation.valid && (
            <div className="training-constraint-error">{constraintValidation.error}</div>
          )}
        </section>
      )}

      {showForceBid && onForceBid && (
        <section className="training-deal-setup-section">
          <h4 className="training-deal-setup-title">Forcer le contrat (enchères en cours)</h4>
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              className="training-select"
              value={forceSeat}
              onChange={(e) => onForceSeatChange?.(Number(e.target.value))}
              style={{ flex: 1 }}
            >
              {(players ?? [0, 1, 2, 3].map((p) => ({ pseudo: POSITION_NAMES[p], position: p }))).map(
                (p) => (
                  <option key={p.position} value={p.position}>
                    {p.pseudo} ({POSITION_NAMES[p.position] ?? `P${p.position}`})
                  </option>
                ),
              )}
            </select>
            <select
              className="training-select"
              value={forceContract}
              onChange={(e) => onForceContractChange?.(Number(e.target.value))}
              style={{ flex: 1 }}
            >
              {CONTRACT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="training-btn training-btn-secondary"
            onClick={onForceBid}
            style={{ marginTop: 6, width: '100%' }}
          >
            Appliquer
          </button>
        </section>
      )}

      {(onSpeedChange || onToggleBot || (isAdmin && onAIModelSelectionChange)) && (
        <section className="training-deal-setup-section">
          <h4 className="training-deal-setup-title">Session</h4>
          {onSpeedChange && gameSpeed !== undefined && (
            <div className="training-hand-config-row">
              <label htmlFor="training-setup-speed">Vitesse</label>
              <select
                id="training-setup-speed"
                className="training-select"
                value={gameSpeed}
                onChange={(e) => onSpeedChange(e.target.value as GameSpeed)}
              >
                {SPEED_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {onToggleBot && disabledBots && (
            <div className="training-deal-setup-bots">
              <div className="training-toggle-label" style={{ fontSize: 11, marginBottom: 6 }}>
                Jouer à la place d&apos;un bot
              </div>
              {[1, 2, 3].map((seat) => (
                <label key={seat} className="training-deal-setup-check">
                  <input
                    type="checkbox"
                    checked={disabledBots.has(seat)}
                    onChange={() => onToggleBot(seat)}
                  />
                  {POSITION_NAMES[seat]}
                  {humanAutomated && seat === 0 ? ' (automatisé)' : ''}
                </label>
              ))}
            </div>
          )}

          {isAdmin && aiModelSelection && onAIModelSelectionChange && (
            <div style={{ marginTop: 8 }}>
              <div className="training-toggle-label" style={{ fontSize: 11, marginBottom: 6 }}>
                Moteurs IA utilisés
              </div>
              {(
                [
                  ['attacker', 'Attaque'],
                  ['defender', 'Défense'],
                  ['bidding', 'Contrat'],
                  ['discard', 'Écart'],
                ] as Array<[keyof TrainingAIModelSelection, string]>
              ).map(([key, label]) => (
                <div key={key} className="training-hand-config-row">
                  <label>{label}</label>
                  <select
                    className="training-select"
                    value={aiModelSelection[key]}
                    onChange={(e) =>
                      handleAIModelChange(key, e.target.value as TrainingAIModelSlot)
                    }
                  >
                    {AI_MODEL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export { validateHandQualities, isTakerConstraintActive };
